import { useMemo } from 'react';
import type {} from '@react-three/fiber';
import * as THREE from 'three';
import type { Fen, HeatmapMode } from '@strategy4kids/protocol';
import { computeTerritories, controlScores, heatForControl, type Territory } from './control.js';
import { PowerMap } from './PowerMap.js';
import { squareToVec3 } from './scene.js';

const WHITE_TINT = new THREE.MeshBasicMaterial({ color: '#3b82f6', transparent: true, opacity: 0.4, depthWrite: false });
const BLACK_TINT = new THREE.MeshBasicMaterial({ color: '#ef4444', transparent: true, opacity: 0.4, depthWrite: false });
const squarePlane = new THREE.PlaneGeometry(0.95, 0.95);

function territoryBounds(t: Territory): { x: number; z: number; w: number; h: number } {
  let minF = 8; let maxF = -1; let minR = 9; let maxR = -1;
  for (const s of t.squares) {
    const f = s.charCodeAt(0) - 97; const r = Number(s[1]);
    minF = Math.min(minF, f); maxF = Math.max(maxF, f);
    minR = Math.min(minR, r); maxR = Math.max(maxR, r);
  }
  const cx = 3.5 - (minF + maxF) / 2;
  const cz = 3.5 - (8 - (minR + maxR) / 2);
  return { x: cx, z: cz, w: maxF - minF + 1, h: maxR - minR + 1 };
}

/**
 * Dispatcher for the board overlay plane.
 *
 * `power` is not a heat map at all — it depends on the coordinate, not the
 * position — but it shares this one board-wide wash so the player cycles four
 * states through one button instead of stacking independently-toggled overlays.
 *
 * This component deliberately holds **no hooks**. Selecting a mode mounts a
 * different child, and the two position-derived modes share a component whose
 * `useMemo` is unconditional; calling a hook after a mode-dependent early
 * return would tear down the hook order mid-session the first time a kid cycles
 * from power to control.
 */
export function HeatMap({ fen, mode }: { fen: Fen; mode: HeatmapMode }) {
  if (mode === 'off') return null;
  if (mode === 'power') return <PowerMap />;
  return <PositionHeatMap fen={fen} mode={mode} />;
}

/** The control and territory washes — both a function of the position. */
function PositionHeatMap({ fen, mode }: { fen: Fen; mode: 'control' | 'territory' }) {
  const scores = useMemo(() => controlScores(fen), [fen]);

  if (mode === 'control') {
    const cells = Object.entries(scores)
      .filter(([, s]) => s !== 0)
      .map(([square, score]) => ({ square, heat: heatForControl(score) }));
    return (
      <group>
        {cells.map(({ square, heat }) => {
          const [x, , z] = squareToVec3(square);
          const mat = heat.tint === 'w' ? WHITE_TINT : BLACK_TINT;
          return (
            <mesh key={square} geometry={squarePlane} position={[x, 0.05, z]} rotation={[-Math.PI / 2, 0, 0]} material={mat}>
              {/* uniform opacity — per-cell opacity would need a cloned material per square */}
            </mesh>
          );
        })}
      </group>
    );
  }

  const territories = computeTerritories(scores);
  return (
    <group>
      {territories.map((t) => {
        const { x, z, w, h } = territoryBounds(t);
        const mat = t.owner === 'w' ? WHITE_TINT : BLACK_TINT;
        // Reuse the shared unit plane and scale it (0.95 base → integer-size tiles) so no
        // geometry is allocated per render.
        return <mesh key={`terr-${t.squares[0]}`} geometry={squarePlane} scale={[Math.min(7, w), Math.min(7, h), 1]} position={[x, 0.06, z]} rotation={[-Math.PI / 2, 0, 0]} material={mat} />;
      })}
    </group>
  );
}
